import React from 'react';
import { List } from 'immutable';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { reduxForm, Field, formValueSelector } from 'redux-form/immutable';
import { Divider, Grid, FormControlLabel, Switch, Typography, Button } from '@material-ui/core';

import config from 'config';
import { lightGray, primaryBlue, gray2, red } from 'utils/color-styles';
import { isURL, isHexColor } from 'utils/validators';
import Box from 'retired/Box';
import FormCheckbox from 'retired/FormCheckbox';
import FormFieldLabel from 'retired/FormFieldLabel';
import FormTextField from 'retired/FormTextField';
import FormIncludeExcludeSelector from 'retired/FormIncludeExcludeSelector';
import FormLocationSelector from 'retired/FormLocationSelector';
import { selectAccessibleUserLocations } from 'entities/user/selectors';
import { ViewInAppButton } from 'components/ViewInAppButton';

import { NOTIFICATION_BANNER_FORM_IDENTIFIER as FORM_IDENTIFIER } from './constants';

const isUrlValidator = isURL();
const isHexColorValidator = isHexColor();

const Banner = styled.div<{ backgroundColor: string }>`
  background-color: ${({ backgroundColor }) => backgroundColor};
  padding: 15px;
  margin: 15px 0;
`;

const BannerText = styled.div<{ textColor: string }>`
  color: ${({ textColor }) => textColor};
  text-align: center;
  font-size: 14px;
  font-family: Graphik;
  font-weight: 500;
`;

const withForm = reduxForm({
  form: FORM_IDENTIFIER,
  destroyOnUnmount: false,
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
});

const valueSelector = formValueSelector(FORM_IDENTIFIER);

interface NotificationBannerProps {
  submitting: boolean;
  invalid: boolean;
  pristine: boolean;
  handleSubmit: (
    event: React.FormEvent<HTMLFormElement>
  ) => ((event: React.FormEvent<HTMLFormElement>) => void) | undefined;
  submitForm: () => void;
  change: (name: string, value: any) => void;
}

const validateLocations = (value: List<string>, entity: any): string | undefined => {
  if (entity && !entity.get('locationsExclude') && !value?.size) {
    return 'You selected to only include certain locations and none are selected';
  }
  return undefined;
};

const NotificationBannerForm = withForm(
  ({
    submitting,
    handleSubmit,
    submitForm,
    change,
  }: NotificationBannerProps): React.ReactElement => {
    const linkIsActive = useSelector((state) => valueSelector(state, 'linkIsActive'));
    const textColor = useSelector((state) => valueSelector(state, 'textColor'));
    const backgroundColor = useSelector((state) => valueSelector(state, 'backgroundColor'));
    const isActive = useSelector((state) => valueSelector(state, 'isActive'));
    const bannerText = useSelector((state) => valueSelector(state, 'bannerText'));
    const linkText = useSelector((state) => valueSelector(state, 'linkText'));
    const linkUrl = useSelector((state) => valueSelector(state, 'linkUrl'));

    const locations = useSelector(selectAccessibleUserLocations);

    return (
      <form onSubmit={handleSubmit(submitForm)}>
        <fieldset>
          <Box style={{ paddingTop: 0, marginBottom: 30 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
              }}
            >
              <div>
                <h2>Notification Banner</h2>
                <p>A persistent banner on every page across the desktop and mobile experience.</p>
              </div>
              <div>
                {/* eslint-disable-next-line react/jsx-no-target-blank */}
                <ViewInAppButton
                  href={config.clientUrl}
                  disabled={!isActive}
                  iconSize={18}
                  style={{
                    display: 'inline-block',
                    marginRight: 30,
                    marginTop: 25,
                    color: isActive ? primaryBlue : gray2,
                  }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={isActive || false}
                      color="primary"
                      onChange={(e, checked) => change('isActive', checked)}
                      name="isActive"
                    />
                  }
                  label={isActive ? 'Active' : 'Inactive'}
                  style={{ width: 120 }}
                />
              </div>
            </div>

            <Divider style={{ marginBottom: 20 }} />
            {(bannerText || linkIsActive) && (
              <div>
                <FormFieldLabel>Banner Preview</FormFieldLabel>
                <Banner backgroundColor={backgroundColor || red}>
                  <BannerText textColor={textColor || 'white'}>
                    {bannerText}
                    {linkIsActive && linkText && linkUrl ? (
                      <>
                        {' '}
                        {/* eslint-disable-next-line react/jsx-no-target-blank */}
                        <a
                          style={{
                            fontSize: 14,
                            fontFamily: 'Graphik',
                            whiteSpace: 'nowrap',
                            fontWeight: 600,
                            textDecoration: 'underline',
                            color: textColor || 'white',
                          }}
                          href={linkUrl}
                          target="_blank"
                        >
                          {linkText}
                        </a>
                      </>
                    ) : null}
                  </BannerText>
                </Banner>
              </div>
            )}
            <Grid container spacing={4}>
              <Grid item xs={12} xl={6}>
                <FormFieldLabel forName="bannerText">Banner Text</FormFieldLabel>
                <Field
                  id="bannerText"
                  name="bannerText"
                  component={FormTextField}
                  inputProps={{ maxLength: 100 }}
                  multiline
                  fullWidth
                  label="Max 100 Characters. Please use link for additional messaging."
                />
              </Grid>
            </Grid>
            <Field
              name="linkIsActive"
              component={FormCheckbox}
              label="Include Link in Banner • Will display after banner text"
            />
            {linkIsActive && (
              <div
                style={{
                  padding: 30,
                  backgroundColor: lightGray,
                  borderRadius: 4,
                  marginBottom: 20,
                }}
              >
                <Grid container spacing={4}>
                  <Grid item xs={12} md={6}>
                    <FormFieldLabel forName="linkText">Link Text</FormFieldLabel>
                    <Field
                      id="linkText"
                      name="linkText"
                      component={FormTextField}
                      inputProps={{ maxLength: 50 }}
                      fullWidth
                      label="Max 50 Characters."
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormFieldLabel forName="linkUrl">Link URL</FormFieldLabel>
                    <Field
                      id="linkUrl"
                      name="linkUrl"
                      component={FormTextField}
                      fullWidth
                      label="Can be Deed or external link"
                      validate={isUrlValidator}
                    />
                  </Grid>
                </Grid>
              </div>
            )}
            <Grid container spacing={4} style={{ marginBottom: 20 }}>
              <Grid item xs={12} md={6}>
                <FormFieldLabel forName="backgroundColor">Background Color</FormFieldLabel>
                <div style={{ position: 'relative' }}>
                  <div
                    style={{
                      width: '20px',
                      height: '20px',
                      backgroundColor,
                      borderRadius: '3px',
                      border: '1px solid rgba(0, 0, 0, .5)',
                      position: 'absolute',
                      bottom: 11,
                      right: 0,
                    }}
                  />
                  <Field
                    id="backgroundColor"
                    name="backgroundColor"
                    component={FormTextField}
                    inputProps={{ maxLength: 7 }}
                    fullWidth
                    validate={isHexColorValidator}
                    label="Please use a HEX code (e.g. #FFFFFF)"
                  />
                </div>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormFieldLabel forName="textColor">Text Color</FormFieldLabel>
                <div style={{ position: 'relative' }}>
                  <div
                    style={{
                      width: '20px',
                      height: '20px',
                      backgroundColor: textColor,
                      borderRadius: '3px',
                      border: '1px solid rgba(0, 0, 0, .5)',
                      position: 'absolute',
                      bottom: 11,
                      right: 0,
                    }}
                  />
                  <Field
                    id="textColor"
                    name="textColor"
                    component={FormTextField}
                    inputProps={{ maxLength: 7 }}
                    fullWidth
                    validate={isHexColorValidator}
                    label="Please use a HEX code (e.g. #FFFFFF)"
                  />
                </div>
              </Grid>
            </Grid>

            <div style={{ marginBottom: 30 }}>
              <FormIncludeExcludeSelector
                name="locationsExclude"
                labelText="the following locations:"
              />
              <Field
                name="locations"
                component={FormLocationSelector}
                props={{ locations }}
                validate={validateLocations}
              />
            </div>

            <Typography align="right">
              <Button type="submit" variant="contained" color="primary" disabled={submitting}>
                Submit
              </Button>
            </Typography>
          </Box>
        </fieldset>
      </form>
    );
  }
);

export default NotificationBannerForm;
